import { AxiosError } from 'axios';
import { apiService } from './api';

/** Mirrors the backend InstallmentFrequency enum (serialised as an int). */
export enum InstallmentFrequency {
  Weekly = 0,
  Monthly = 1,
}

/** Mirrors the backend InstallmentScheduleStatus enum (serialised as an int). */
export enum InstallmentScheduleStatus {
  Pending = 0,
  PartiallyPaid = 1,
  Paid = 2,
  Overdue = 3,
}

export interface InstallmentScheduleRow {
  id: number;
  sequenceNumber: number;
  dueDate: string;
  amount: number;
  paidAmount: number;
  status: InstallmentScheduleStatus;
}

export interface InstallmentPlan {
  id: number;
  salesInvoiceId: number;
  numberOfInstallments: number;
  frequency: InstallmentFrequency;
  schedule: InstallmentScheduleRow[];
}

export interface CreateInstallmentPlanDTO {
  numberOfInstallments: number;
  frequency: InstallmentFrequency;
  /** Due date of the first installment; later rows step forward by the frequency. */
  firstDueDate: string;
}

/**
 * Installment plans hang off a sales invoice rather than being a resource of their own,
 * so every call is scoped by invoice id. Payments are never posted here — the plan is
 * settled through the normal invoice payment path, which allocates against the schedule.
 */
const installmentService = {
  /** Returns null when the invoice has no plan yet, which the API reports as a 404. */
  getByInvoiceId: async (invoiceId: number) => {
    try {
      const response = await apiService.get<InstallmentPlan>(`/sales-invoices/${invoiceId}/installment-plan`);
      return response.data.data ?? null;
    } catch (err) {
      if ((err as AxiosError).response?.status === 404) return null;
      throw err;
    }
  },

  create: async (invoiceId: number, dto: CreateInstallmentPlanDTO) => {
    const response = await apiService.post<InstallmentPlan>(
      `/sales-invoices/${invoiceId}/installment-plan`,
      dto
    );
    return response.data.data;
  },
};

export default installmentService;
